import {
  Box,
  Flex,
  freddyTheme,
  GridItem,
  Link,
  List,
  ListItem,
  Typography
} from '@coolblue-development/freddy';
import clickOnChangeTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeInOverlay/clickOnChangeTrigger';
import { RecycleCircled } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import { SalesPrice, SalesPriceCurrent } from '@coolblue-development/next-sales-price/client';
import { useTranslation } from '@i18n/useTranslation';
import { useContext } from 'react';

import { ProductTradeInContext } from '../Context';

const PRODUCT_STEP_INDEX = 1;
const CONDITION_STEP_INDEX = 2;

const Summary = () => {
  const {
    entry,
    currentStep,
    options,
    productType,
    salesPrice = 0,
    summary,
    variantChoices,
    goToStep
  } = useContext(ProductTradeInContext);
  const { t } = useTranslation('webshop.productTradeIn');
  const { trackEvent } = useAnalytics();

  const selectedVariant = options?.find(option => {
    return !option.variances
      .map((variance, index) => variance.value === variantChoices?.[index])
      .some(result => !result);
  });
  const productImages = entry?.productImages;
  const imageId = productImages?.[`product_${selectedVariant?.productIdForImage}`]?.primaryImage.id;

  function handleModifyButtonClick(
    targetStep: typeof PRODUCT_STEP_INDEX | typeof CONDITION_STEP_INDEX
  ) {
    trackEvent(
      clickOnChangeTrigger({
        sub_feature: targetStep === PRODUCT_STEP_INDEX ? 'product' : 'condition'
      })
    );

    goToStep?.(targetStep);
  }

  return (
    <GridItem row={{ xs: '1', md: 'initial' }}>
      <Typography variant='h3' mt={600}>
        {t('summaryTitle')}
      </Typography>
      <Box
        mt={300}
        p={400}
        sx={theme => ({
          borderRadius: theme.borderRadius[100],
          border: `${theme.borderWidth[25]} solid ${theme.color.border.base}`
        })}
      >
        <Flex justifyContent='space-between'>
          <Typography variant='h5'>{t('yourProductTitle')}</Typography>

          <Link component='button' onClick={() => handleModifyButtonClick(PRODUCT_STEP_INDEX)}>
            {t('modifyButtonLabel')}
          </Link>
        </Flex>

        <Flex alignItems='center' mt={200}>
          {imageId && (
            <SquareProductImage
              fileCollection='product'
              alt={variantChoices?.join(' - ') || ''}
              imageId={String(imageId)}
              size='medium'
              loading='eager'
              background='transparent'
            />
          )}

          <List orientation='horizontal' withDivider marker='none' ml={imageId ? '1rem' : 0}>
            {variantChoices?.map(variantChoice => (
              <ListItem key={variantChoice}>{variantChoice}</ListItem>
            ))}
          </List>
        </Flex>

        {currentStep === 3 && (
          <>
            <Flex
              direction='column'
              sx={theme => ({
                border: `${theme.borderWidth[25]} solid ${theme.color.border.base}`,
                borderRight: 'none',
                borderLeft: 'none'
              })}
              py={400}
              px='0'
              mt={400}
            >
              <Flex justifyContent='space-between'>
                <Typography variant='h5'>{t('steps.condition')}</Typography>

                <Link
                  component='button'
                  onClick={() => handleModifyButtonClick(CONDITION_STEP_INDEX)}
                >
                  {t('modifyButtonLabel')}
                </Link>
              </Flex>

              <Typography variant='body' mt={100}>
                {t(`conditions.${summary?.priceLevel}`)}
              </Typography>
            </Flex>

            <Flex direction='column' mt={400}>
              <Flex justifyContent='space-between'>
                <Typography variant='h5'>{t('tradingValueTitle')}</Typography>

                {summary?.tradeInPrice && summary?.tradeInPrice > 0 ? (
                  <SalesPrice
                    size='small'
                    currentPrice={<SalesPriceCurrent price={summary?.tradeInPrice} />}
                  />
                ) : null}
              </Flex>

              <Flex justifyContent='space-between' alignItems='center' mt={100}>
                <Flex gap={200} alignItems='center'>
                  {summary?.tradeInPrice === 0 ? (
                    <RecycleCircled fill={freddyTheme.color.icon.base} />
                  ) : undefined}

                  <Typography my='0'>
                    {summary?.tradeInPrice === 0
                      ? t('recycleYourProduct')
                      : t('newPriceForYourProduct', {
                          productType
                        })}
                  </Typography>
                </Flex>

                {summary?.tradeInPrice && summary?.tradeInPrice > 0 ? (
                  <SalesPrice
                    size='small'
                    currentPrice={
                      <SalesPriceCurrent isSubtle price={salesPrice - summary?.tradeInPrice} />
                    }
                  />
                ) : null}
              </Flex>
            </Flex>
          </>
        )}
      </Box>
    </GridItem>
  );
};

export default Summary;
